<template>
  <header id="tool-bar">
    <div id="report-header">
      <div class="back-button" @click="onBackButtonClick">
        <img src="/img/icons/back_arrow.png" />
      </div>
      <div class="breadcrumb-outer">
        <span
          :hidden="this.$route.params.token || this.user._id === this.oilUser"
          id="gotoProductList"
          @click="gotoProductList"
          class="breadcrumb-text"
        >
          {{ $t('work_order_list') }}
        </span>

        <span class="divider-text">/</span>

        {{ jobWorkName }}

        <span class="divider-text">/</span>

        <b-dropdown
          aria-role="list"
          v-model="selectedTask"
          @change="onChangeItem"
          :close-on-click="true"
          :scrollable="true"
          :max-height="500"
        >
          <template #trigger="{ active }">
            <div class="container-select">
              <div class="dropdown-input-container">
                <input
                  type="text"
                  class="dropdown-input"
                  @mousedown="preventMouseDown"
                  :value="dropdownInputText"
                />

                <b-icon
                  :icon="active ? 'menu-up' : 'menu-down'"
                  size="is-small"
                />
              </div>
            </div>
          </template>

          <template v-for="task in dropDownTasks">
            <b-dropdown-item
              aria-role="listitem"
              :value="task._id"
              id="task-dropdown-item"
              v-if="task.taskType === 'section'"
            >
              <div :class="isTaskExclude(task._id) && 'excluded'">
                <b-icon :icon="'pound'" />
                {{ task.taskName }}
              </div>
            </b-dropdown-item>

            <b-dropdown-item
              aria-role="listitem"
              :value="task._id"
              id="task-dropdown-item"
              v-if="task.taskType !== 'folder' && task.taskType !== 'section'"
            >
              <div
                :style="{
                  'padding-left': getIndentationLevel(task.folderLevel),
                }"
                :class="isTaskExclude(task._id) && 'excluded'"
              >
                <b-checkbox
                  v-if="enableExcludeItems"
                  :value="isTaskExclude(task._id)"
                  @click.native.capture="stopPropagation"
                  @input="(e) => onExcludeTask(e, task)"
                ></b-checkbox>
                <b-icon
                  :icon="
                    task.taskType === 'instruction'
                      ? 'format-list-bulleted'
                      : 'wrench'
                  "
                />
                {{ task.taskName }}
              </div>
            </b-dropdown-item>

            <b-dropdown-item
              aria-role="listitem"
              :value="task._id"
              id="task-dropdown-item"
              v-if="task.taskType === 'folder'"
              :disabled="true"
            >
              <div
                :style="{
                  'padding-left': getIndentationLevel(task.folderLevel),
                }"
              >
                <b-checkbox
                  v-if="enableExcludeItems"
                  @input="(e) => onExcludeFolder(e, task._id)"
                  :value="isTaskExclude(task._id)"
                ></b-checkbox>
                <b-icon :icon="'folder'" />
                {{ task.taskName }}
              </div>
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </div>

      <b-dropdown
        aria-role="list"
        class="header-button"
        v-model="reportType"
        position="is-bottom-right"
      >
        <template #trigger="{ active }">
          <b-button
            size="is-small"
            :icon-right="active ? 'menu-up' : 'menu-down'"
          >
            {{ reportTypeLabel }}
          </b-button>
        </template>
        <b-dropdown-item aria-role="listitem" value="internal">
          {{ $t('report_header.internal_report') }}
        </b-dropdown-item>
        <b-dropdown-item aria-role="listitem" value="customer">
          {{ $t('report_header.customer_report') }}
        </b-dropdown-item>
      </b-dropdown>
      <b-button
        size="is-small"
        class="header-button"
        :focused="false"
        @click="onExcludeItems"
      >
        <template v-if="enableExcludeItems"> {{ $t('done') }} </template>
        <template v-else>{{ $t('report_header.exclude_items') }}</template>
      </b-button>
    </div>
    <div class="bubble-unit">
      <b-button
        :type="displayUnit === 'mm' ? 'is-primary' : ''"
        @click="setUnit('mm')"
        >mm
      </b-button>
      <b-button
        :type="displayUnit === 'in' ? 'is-primary' : ''"
        @click="setUnit('in')"
        >inch
      </b-button>
    </div>
    <div class="download-container" :class="{ disabled: computedIsOffline }">
      <div class="report-options-container">
        <b-dropdown
          aria-role="list"
          id="report-options-dropdown"
          position="is-bottom-left"
          :scrollable="true"
          :max-height="500"
          :disabled="computedIsOffline"
        >
          <template #trigger="{ active }">
            <b-button
              type="is-info"
              outlined
              icon-left="menu-open"
              :icon-right="active ? 'menu-up' : 'menu-down'"
              :disabled="computedIsOffline"
            >
              {{ $t('report_header.options') }}</b-button
            >
          </template>

          <b-dropdown-item aria-role="listitem" @click="showProgressModal">
            <div class="option-item">
              <b-icon icon="progress-check" />
              {{ $t('report_header.progress') }}
            </div>
          </b-dropdown-item>

          <b-dropdown-item
            aria-role="listitem"
            @click="showUsedInstrumentationModal"
          >
            <div class="option-item">
              <img class="option-img-icon" src="/img/diagram_icon.png" />
              {{ $t('report_header.instrument_log') }}
            </div>
          </b-dropdown-item>

          <b-dropdown-item aria-role="listitem" @click="showDocumentsModal">
            <div class="option-item">
              <b-icon icon="file-document-arrow-right" />
              {{ $t('report_header.documents') }}
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div>
        <b-dropdown
          aria-role="list"
          id="revision-dropdown"
          position="is-bottom-left"
          :scrollable="true"
          :max-height="500"
          :disabled="computedIsOffline"
        >
          <template #trigger="{ active }">
            <b-button
              :icon-right="active ? 'menu-up' : 'menu-down'"
              :disabled="computedIsOffline"
              >{{ $t('report_header.revisions') }}</b-button
            >
          </template>

          <b-dropdown-item
            aria-role="listitem"
            v-for="report in generatedReports"
            :key="report.id"
            @click="onDownloadGeneratedReport(report.id)"
          >
            <div class="columns">
              <div class="column">
                <div style="display: flex; justify-content: space-between">
                  <p>
                    {{
                      $t('report_header.description', {
                        description: report.revisionDescription,
                      })
                    }}
                  </p>
                  <b-button
                    type="is-danger"
                    icon-right="delete"
                    :disabled="!canEditReport"
                    @click.stop="onDeleteRevision(report)"
                  />
                </div>

                <div class="columns">
                  <div class="column">{{ report.fileName }}</div>
                  <div class="column">{{ parseDate(report.date) }}</div>
                </div>
              </div>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div>
        <b-button
          size="is-large"
          type="is-info"
          @click="onDownload"
          :disabled="
            disabledDownload || computedIsOffline || !canDownloadReports
          "
          :loading="disabledDownload"
          >{{ $t('report_header.download') }}</b-button
        >
      </div>
    </div>
    <documents-modal
      v-if="isDocumentModalVisible"
      :toolType="toolType"
      :canEditDocuments="canEditReport"
      @close="closeDocumentModal"
    />

    <progress-modal
      v-if="isProgressModalVisible"
      :tool="toolType"
      @onClose="closeProgressModal"
    />

    <used-instrumentations-modal
      v-if="isUsedInstrumentationModalVisible"
      @close="closeUsedInstrumentationModal"
    />
  </header>
</template>

<script>
import {
  REPORTS_TOGGLE_EXCLUDE_ITEMS,
  REPORTS_SET_REPORT_TYPE,
  REPORTS_DELETE_REVISION,
} from '../../store/reports/actions/actionTypes';
import ExcludeItemsMixin from '../../mixins/Report/ExcludeItemsMixin';
import client from '../../config/api';
import DocumentsModal from '../../components/Shared/DocumentsModal';
import ProgressModal from '../../components/Shared/ProgressModal';
import UsedInstrumentationsModal from '../../components/Shared/Instrumentations/UsedInstrumentationsModal';
import OfflineMixin from '../../mixins/OfflineMixin';
import { GET_CURRENT_DISPLAY_UNIT } from '../../store/diagram/getters/getterTypes';
import { SET_CURRENT_DISPLAY_UNIT } from '../../store/diagram/mutations/mutationsTypes';

export default {
  name: 'report-header',
  components: {
    DocumentsModal,
    ProgressModal,
    UsedInstrumentationsModal,
  },
  props: {
    onDownload: {
      type: Function,
    },
    disabledDownload: {
      type: Boolean,
      default: false,
    },
    closestHeaderSection: {
      type: String,
      default: null,
    },
    jobWorkName: {
      type: String,
      default: '',
    },
  },
  mixins: [ExcludeItemsMixin, OfflineMixin],

  data() {
    return {
      oilUser: '',
      selectedTask: '',
      isDocumentModalVisible: false,
      isProgressModalVisible: false,
      isUsedInstrumentationModalVisible: false,
      toolType: 'report',
    };
  },

  watch: {
    closestHeaderSection(value) {
      if (value) {
        this.selectedTask = value;
      }
    },
  },

  computed: {
    user() {
      return this.$store.state.currentUser;
    },

    job() {
      return this.$store.state.JobsModule.currentJob;
    },

    tasks() {
      const tasks = this.$store.state.ReportsModule?.reportView?.tasks;
      return Array.isArray(tasks) ? tasks : [];
    },

    isInternalReport() {
      return this.$store.getters['ReportsModule/isInternalReport'];
    },

    reportType: {
      get() {
        return this.$store.state.ReportsModule.reportType;
      },
      set(value) {
        this.$store.dispatch(REPORTS_SET_REPORT_TYPE, value);
      },
    },

    reportTypeLabel() {
      const labels = {
        internal: this.$t('report_header.internal_report'),
        customer: this.$t('report_header.customer_report'),
      };
      return labels[this.reportType] || labels.internal;
    },

    hierarchy() {
      return this.buildTaskHierarchy([...this.tasks]);
    },

    dropDownTasks() {
      const dropdownItems = [
        {
          _id: 'dij-header-section',
          taskName: this.$t('report_header.header'),
          taskType: 'section',
        },
        {
          _id: 'dij-deviation-report-container',
          taskName: this.$t('report_header.deviation_report'),
          taskType: 'section',
        },
        {
          _id: 'dij-repair-assessment',
          taskName: this.$t('report_header.repair_assessment'),
          taskType: 'section',
        },
        {
          _id: 'dij-work-performed',
          taskName: this.$t('report_header.work_performed'),
          taskType: 'section',
        },
      ];
      if (this.job.childConfig === undefined) return [];

      if (this.tasks.length > 0) {
        dropdownItems.push(...this.flattenHierarchy(this.hierarchy));
        return dropdownItems;
      }
      return [];
    },

    dropdownInputText() {
      if (this.tasks.length === 0) return '';
      const task = this.dropDownTasks.find((t) => t._id === this.selectedTask);
      if (task) return task.taskName;
      if (this.tasks) return this.dropDownTasks[0].taskName;
    },

    generatedReports() {
      if (!this.job.reports) return [];
      return this.job.reports
        .filter((report) => {
          if (report.readyToDownload && report.date && report.preparedBy)
            return report;
        })
        .reverse();
    },
    displayUnit() {
      return this.$store.getters[GET_CURRENT_DISPLAY_UNIT];
    },
    canDownloadReports() {
      return this.$store.getters.hasPermission('report.download');
    },
    canEditReport() {
      return this.$store.getters.canEditJob(this.job, 'report');
    },
  },
  async mounted() {
    this.oilUser = process.env.VUE_APP_OIL_USER_EMAIL;
    const defaultUnit = this.user?.unit === 'inch' ? 'in' : 'mm';
    this.$store.commit(SET_CURRENT_DISPLAY_UNIT, defaultUnit);
  },

  methods: {
    onDeleteRevision(revision) {
      if (!this.canEditReport) {
        return;
      }
      this.$buefy.dialog.confirm({
        title: this.$t('report_header.deleting_report_revision'),
        message: this.$t('report_header.delete_revision_confirmation', {
          fileName: revision.fileName,
          description: revision.revisionDescription,
        }),
        confirmText: this.$t('report_header.delete_revision'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () =>
          this.$store.dispatch(REPORTS_DELETE_REVISION, {
            jobId: this.job._id,
            revision,
          }),
      });
    },

    async onDownloadGeneratedReport(reportId) {
      try {
        const response = await client.get(
          `/job/${this.job._id}/presign-report/${reportId}`
        );
        if (response.status === 200) {
          this.triggerDownload(response.data);
        }
      } catch (error) {
        console.error('Error checking server status:', error);
      }
    },

    triggerDownload(downloadLink) {
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = downloadLink;
      anchor.setAttribute('download', '');

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },
    onBackButtonClick() {
      this.$router.push('/view/report');
    },
    gotoProductList() {
      this.$router.push('/view/report');
    },
    preventMouseDown(event) {
      event.preventDefault();
    },
    buildTaskHierarchy(tasks) {
      const tasksMap = new Map();
      const hierarchy = [];

      // Sort tasks based on taskType and folderId
      tasks.sort((a, b) => {
        if (a.folderId && !b.folderId) {
          return 1; // Folder comes after root task
        }
        if (!a.folderId && b.folderId) {
          return -1; // Root task comes before folder
        }
        return 0; // Maintain the original order
      });

      tasks.forEach((task) => {
        task.children = [];
        tasksMap.set(task._id, task);
      });

      // Traverse the tasks and assign children to their parent folders
      tasks.forEach((task) => {
        if (task.folderId) {
          const parentTask = tasksMap.get(task.folderId);
          if (parentTask) {
            parentTask.children.push(task);
          }
        } else {
          hierarchy.push(task); // Task is at the root level
        }
      });

      return hierarchy;
    },
    flattenHierarchy(hierarchy, folderLevel = 0) {
      const flattened = [];

      hierarchy.forEach((task) => {
        task.folderLevel = folderLevel;
        flattened.push(task);

        if (task.children && task.children.length) {
          const children = this.flattenHierarchy(
            task.children,
            folderLevel + 1
          );
          flattened.push(...children);
        }
      });

      return flattened;
    },
    getIndentationLevel(level) {
      if (!level) {
        return '0px';
      }
      return `${(level || 0) * 25}px`;
    },
    onExcludeItems() {
      this.$store.dispatch(REPORTS_TOGGLE_EXCLUDE_ITEMS);
    },
    onChangeItem(e) {
      const element = document.getElementById(e);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    onExcludeTask(e, task) {
      if (e === false) {
        if (task.folderId) {
          this.onTaskToExclude(false, task.folderId);
        }
      }
      this.onTaskToExclude(e, task._id);
    },
    onExcludeFolder(value, folderId) {
      this.dropDownTasks.forEach((task) => {
        if (task._id === folderId) {
          this.onTaskToExclude(value, task._id);
          if (task.children) {
            task.children.forEach((t) => {
              this.onTaskToExclude(value, t._id);
              if (t.children) {
                this.onExcludeFolder(value, t._id);
              }
            });
          }
          if (task.folderId && value === false) {
            this.onTaskToExclude(false, task.folderId);
          }
        }
      });
    },
    stopPropagation(e) {
      e.stopPropagation();
    },
    parseDate(date) {
      return new Date(date).toLocaleDateString([]);
    },
    showDocumentsModal() {
      this.isDocumentModalVisible = true;
    },
    closeDocumentModal() {
      this.isDocumentModalVisible = false;
      // reload the page
      window.location.reload();
    },
    showProgressModal() {
      this.isProgressModalVisible = true;
    },
    closeProgressModal() {
      this.isProgressModalVisible = false;
    },
    showUsedInstrumentationModal() {
      this.isUsedInstrumentationModalVisible = true;
    },
    closeUsedInstrumentationModal() {
      this.isUsedInstrumentationModalVisible = false;
    },
    setUnit(unit) {
      this.$store.commit(SET_CURRENT_DISPLAY_UNIT, unit);
    },
  },
};
</script>

<style scoped lang="scss">
.dropdown-item {
  border-bottom: 1px solid #e0e0e0;
}

#tool-bar {
  display: flex;
  background-color: #fafafa;
  font-size: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  color: #333;
  border-bottom: 0.1px solid #ccc;
}
#report-header {
  font-family: 'Siemens Sans';
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 100%;
  height: 50px;

  .back-button {
    display: inline-flex;
    width: 80px;
    height: 100%;
    cursor: pointer;
    img {
      width: 25px;
      margin: auto;
      &:hover {
        filter: brightness(0%);
      }
    }
  }

  #gotoProductList {
    color: #ccc;
    &:hover {
      color: inherit;
      cursor: pointer;
    }
  }

  .breadcrumb-outer {
    display: flex;
    align-items: center;
  }

  .breadcrumb-text {
    margin-right: 5px;
  }

  .container-select {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .divider-text {
    margin: 0 5px;
  }

  .dropdown-input {
    height: 27px;
    border: none;
    font-size: 16px;
    background: transparent;
    cursor: default;
  }
  .dropdown-input-container {
    border-bottom: 1px solid #ccc;
  }

  #task-dropdown-item {
    font-size: 16px;
    vertical-align: auto;
  }
}

.dropdown-item.is-active {
  background-color: rgba(33, 33, 33, 0.12);
}

.header-button {
  margin-left: 10px;

  &.dropdown {
    ::v-deep .dropdown-trigger {
      display: flex;
      align-items: center;
    }
  }
}

.download-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  #revision-dropdown {
    margin-right: 10px;
    height: 35px;
  }
}

.report-options-container {
  display: flex;
  align-items: center;
  margin-right: 10px;

  ::v-deep .dropdown-trigger {
    display: flex;
    align-items: center;
  }
}

.excluded {
  text-decoration: line-through;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.option-img-icon {
  width: 20px;
  height: 20px;
}

.bubble-unit {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin: 8px;
}

.bubble-unit button {
  padding: 10px 40px; /* ✅ THIS makes button bigger */
  font-size: 16px; /* ✅ bigger text */
  min-width: 70px;
}
</style>
